import React, { useState } from 'react'

import ButtonIcon from '@/components/ui/ButtonIcon/ButtonIcon'
import Modal from '@/layouts/modal/Modal'
import DotsIcon from '@/assets/icons/dots'
import { EyeIcon, ShareIcon } from '@/assets/icons'
import Toggle from '@/components/ui/toggle'
import { i18n } from '@/locale/i18n'

import { useMarketShare, useMarketFollow } from './MarketShareFollow'

/**
 * Compact share/follow entry point: both actions collapse into a single "..."
 * button that opens a bottom sheet, used where the title row has no space for
 * two icons.
 */
const MarketMoreMenu = ({ question, currentUser }) => {
  const [isOpen, setIsOpen] = useState(false)
  const shareMarket = useMarketShare(question)
  const toggleFollow = useMarketFollow(question, currentUser, {
    onInterrupt: () => setIsOpen(false),
  })

  const itemClassName =
    'flex items-center gap-3 w-full py-4 text-sm font-medium text-Neutral-6 cursor-pointer'

  const handleShare = e => {
    e.preventDefault()
    e.stopPropagation()
    setIsOpen(false)
    shareMarket()
  }

  const handleFollowToggle = () => {
    toggleFollow()
  }

  const actions = (
    <div className="flex flex-col">
      <button type="button" className={itemClassName} onClick={handleShare}>
        <ShareIcon width="20px" height="20px" />
        {i18n('markets.share_market')}
      </button>
      <div
        role="button"
        tabIndex={0}
        className={`${itemClassName} justify-between`}
        onClick={handleFollowToggle}
        onKeyDown={e => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault()
            handleFollowToggle()
          }
        }}>
        <span className="flex items-center gap-3">
          <EyeIcon outline={!question.is_following} width="20px" height="20px" />
          {i18n(question.is_following ? 'markets.following' : 'markets.follow')}
        </span>
        <Toggle active={!!question.is_following} onToggle={handleFollowToggle} />
      </div>
    </div>
  )

  return (
    <>
      <ButtonIcon
        size="medium"
        cursorPointer
        highlightOnHover
        isFocused={isOpen}
        handleOnClick={() => setIsOpen(true)}
        ariaLabel={i18n('markets.more_options')}>
        <DotsIcon fillcolor="currentColor" />
      </ButtonIcon>

      <Modal
        isOpen={isOpen}
        position="bottom"
        onCloseRequested={() => setIsOpen(false)}
        onClose={() => setIsOpen(false)}
        title={i18n('markets.more_options')}
        content={actions}
        containerClass="px-4 !py-0"
        showCloseButton
      />
    </>
  )
}

export default MarketMoreMenu
